import React, { useState, useRef, useEffect } from 'react'
import { useHistory } from 'react-router-dom'
import classNames from 'classnames'
import DateTooltip from '~/components/common/DateTooltip'
import Tooltip from '~/components/common/Tooltip'
import withSimpleApi, { WithSimpleApiProps } from '~/decorators/withSimpleApi'
import { HistorySide } from './activeChat/Side'
import { formatDate } from '~/utils/dateFns'
import Parser from 'html-react-parser'
import { VariableSizeList as List } from 'react-window'
import useResizeObserver from 'use-resize-observer'

type Script = {
  name: string
  value: number
}

type Message = {
  id: number
  createdAt: Date
  client: boolean
  content: string
  user: number
  type: 'client' | 'user' | 'ai' | 'redirect' | 'info' | 'script' | 'part'
  script: Script | number
  redirectToUser: number
}

type Client = {
  id: number
  name: string
  avatar: string
}

export type State = {
  id: number
  client: Client
  createdAt: Date
  system: string
  ip: string
  browser: string
  page: string
  startedAt: Date
  endedAt: Date | null
  location: string
  messages: Message[]
}

const HistoryShow: React.FC<WithSimpleApiProps<State>> = ({ apiData }) => {
  const [chat, setChat] = useState(apiData)
  const [sideActive, setSideActive] = useState(false)
  const history = useHistory()
  const listRef = useRef<any>({})
  const rowHeights = useRef({})

  const { ref, height = 0 } = useResizeObserver<HTMLElement>()

  const setRowHeight = (index, size) => {
    listRef.current.resetAfterIndex(0)
    rowHeights.current = { ...rowHeights.current, [index]: size }
  }

  const getRowHeight = index => {
    return rowHeights.current[index] || 50
  }

  const Row = ({ data: { messages }, index, style }) => {
    const rowRef: any = useRef({})

    useEffect(() => {
      if (rowRef.current) {
        setRowHeight(index, rowRef.current.clientHeight)
      }
    }, [rowRef])

    const user = window.c.users.find(e => e.id == messages[index].user)
    const redirectToUser = window.c.users.find(
      e => e.id == messages[index].redirectToUser
    )

    return (
      <>
        <div style={style}>
          <div
            className="chat-output-row chat-output-row--animation"
            ref={rowRef}
          >
            {messages[index].type === 'part' && (
              <div className="chat-message is-horizontalLine is-date">
                <div>
                  <div></div>
                  <div>{formatDate(messages[index].createdAt, 'PPp')}</div>
                  <div></div>
                </div>
              </div>
            )}
            {messages[index].type === 'user' && (
              <div className="chat-message chat-history-message is-answer">
                <Tooltip content={user?.name}>
                  <div className="chat-history-message-avatar">
                    <img src={user?.avatar} />
                  </div>
                </Tooltip>
                <span className="chat-history-message-content">
                  {Parser(messages[index].content)}
                </span>
              </div>
            )}
            {messages[index].type == 'ai' && (
              <div className="chat-message chat-history-message is-answer">
                <Tooltip content={'ai'}>
                  <div className="chat-history-message-avatar">
                    <img src="/img/ai.png" />
                  </div>
                </Tooltip>
                <span className="chat-history-message-content">
                  {Parser(messages[index].content)}
                </span>
              </div>
            )}
            {messages[index].type === 'client' && (
              <div className="chat-message chat-history-message is-question">
                <Tooltip content={chat.client.name}>
                  <div className="chat-history-message-avatar">
                    <img src={chat.client.avatar} />
                  </div>
                </Tooltip>
                <span className="chat-history-message-content">
                  {Parser(messages[index].content)}
                </span>
              </div>
            )}
            {messages[index].type === 'script' &&
              Array.isArray(messages[index].script) && (
                <div className="chat-message chat-history-message is-question">
                  <span className="chat-history-message-content">
                    <table className="table is-striped is-narrow">
                      {messages[index].script &&
                        messages[index].script.map(sc => (
                          <tr>
                            <th>{sc.name}</th>
                            <td>{sc.value}</td>
                          </tr>
                        ))}
                    </table>
                  </span>
                </div>
              )}
            {messages[index].type == 'info' && messages[index].content && (
              <div className="chat-message is-info">
                {Parser(messages[index].content)}
              </div>
            )}
            {messages[index].type == 'redirect' && (
              <div className="chat-message is-info">
                {user && <strong>{user.name + '  '}</strong>}
                {intl.formatMessage({
                  id: 'chat / history / message / redirect',
                })}
                {redirectToUser && (
                  <strong>{'  ' + redirectToUser.name}</strong>
                )}
              </div>
            )}
          </div>
        </div>
      </>
    )
  }

  return (
    <>
      <div
        className={classNames('chat-activeChat-main', {
          'is-side-active': sideActive,
        })}
      >
        <div className="chat-activeChat-header">
          <button
            onClick={() => {
              history.push('/chat/history')
            }}
            className="chat-activeChat-header-back"
          >
            <span className="far fa-chevron-left" />
          </button>
          <figure className="chat-activeChat-header-avatar">
            <img src={chat.client.avatar} />
          </figure>
          <div className="chat-activeChat-header-name">{chat.client.name}</div>
          <div className="chat-activeChat-header-date">
            <div className="chat-activeChat-header-buttons">
              <button
                className="button is-white is-side"
                onClick={() => setSideActive(v => !v)}
              >
                <span className="icon is-small">
                  <span className="far fa-circle-info" />
                </span>
              </button>
            </div>
            <DateTooltip date={chat.createdAt} />
          </div>
        </div>
        <HistorySide chat={chat} />
        <div className="chat-output" ref={ref}>
          <List
            itemCount={chat.messages.length}
            itemSize={getRowHeight}
            height={height}
            ref={listRef}
            itemData={{ messages: chat.messages }}
          >
            {Row}
          </List>
        </div>
      </div>
      <HistorySide chat={chat} />
    </>
  )
}

const HistoryShowWithApi = withSimpleApi(HistoryShow)

export default HistoryShowWithApi
